import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import { IStoreLocatorPlace } from '@models/IStoreLocatorAutocomplete';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import { IStoreLocatorQueryParams } from '@models/IStoreLocatorQueryParams';
import { IStoreLocatorSearchParams } from '@models/IStoreLocatorSearchParams';
import StoreLocatorAutosuggest from '@molecules/StoreLocatorAutosuggest/StoreLocatorAutosuggest';
import storeLocatorAutocomplete from '@services/storeLocatorAutocomplete';
import storeLocatorSearch from '@services/storeLocatorSearch';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useTabInteractionCheck from '@utils/hooks/useTabInteractionCheck';
import classNames from 'classnames';
import qs from 'qs';
import { useEffect, useState } from 'react';

interface Props {
  readonly autocompleteEndpoint: string;
  readonly countryCode: string;
  readonly defaultLat: string;
  readonly defaultLng: string;
  readonly distanceMeasurementUnitLabel: string;
  readonly distances: number[];
  readonly isInitialLoading?: boolean;
  readonly isListView: boolean;
  readonly language: string;
  readonly searchButtonLabel: string;
  readonly searchEndpoint: string;
  readonly searchPlaceholderLabel: string;
  readonly useMyCurrentLocationLabel: string;
  readonly withinLabel: string;
  readonly onSetIsListView: (isListView: boolean) => void;
  readonly onSetIsInitialLoading: (isInitialLoading: boolean) => void;
  readonly onSuccessfulSearch: (dealers: IStoreLocatorDealer[]) => void;
}

const StoreLocatorSearchBar = ({
  autocompleteEndpoint,
  countryCode,
  defaultLat,
  defaultLng,
  distances,
  distanceMeasurementUnitLabel,
  isInitialLoading,
  isListView,
  language,
  searchButtonLabel,
  searchEndpoint,
  searchPlaceholderLabel,
  useMyCurrentLocationLabel,
  withinLabel,
  onSetIsListView,
  onSetIsInitialLoading,
  onSuccessfulSearch,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQueryText, setCurrentQueryText] = useState('');
  const [currentDistance, setCurrentDistance] = useState<number>(50);
  const [currentPlace, setCurrentPlace] = useState<IStoreLocatorPlace | null>(
    null
  );
  const isTabInteraction = useTabInteractionCheck();

  const handleSearch = async (
    searchParams: IStoreLocatorSearchParams,
    queryText?: string
  ) => {
    try {
      const { latitude, longitude, dealerCardData, locationText } =
        await storeLocatorSearch(searchParams);

      const newQueryText = queryText || locationText || '';
      onSuccessfulSearch(dealerCardData.dealerCards);
      setCurrentPlace(null);
      setCurrentQueryText(newQueryText);

      const newQueryParams = {
        distance: searchParams.distance || 50,
        ...(newQueryText ? { query: newQueryText } : {}),
        ...(parseFloat(latitude) ? { latitude } : {}),
        ...(parseFloat(longitude) ? { longitude } : {}),
      };
      const newQueryString = qs.stringify(newQueryParams, {
        addQueryPrefix: true,
      });
      window.history.pushState({}, '', newQueryString);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });
    } finally {
      onSetIsInitialLoading(false);
      setIsLoading(false);
    }
  };

  const getFirstAutocompletePlace = async (
    searchTerm: string
  ): Promise<IStoreLocatorPlace | null> => {
    try {
      const autoCompleteResponse = await storeLocatorAutocomplete({
        endpoint: autocompleteEndpoint,
        countryCode,
        searchTerm,
      });
      const firstSuggestion = autoCompleteResponse[0];
      const newPlace = firstSuggestion
        ? {
            id: firstSuggestion.placeId,
            name: firstSuggestion.description,
          }
        : null;

      return newPlace;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });
      return null;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const handleInitialSearchOnLoad = async () => {
      onSetIsInitialLoading(true);

      const {
        query: queryParam = '',
        latitude: latitudeParam = '',
        longitude: longitudeParam = '',
        distance: distanceParam = '',
      } = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }) as IStoreLocatorQueryParams;

      const initialDistance = parseInt(distanceParam, 10) || 50;
      setCurrentDistance(initialDistance);

      const initialPlace = queryParam
        ? await getFirstAutocompletePlace(queryParam)
        : null;
      setCurrentPlace(initialPlace);

      await handleSearch(
        {
          endpoint: searchEndpoint,
          input: initialPlace ? '' : queryParam,
          countryCode,
          languageCode: language,
          placeId: initialPlace ? initialPlace.id : '',
          latitude: initialPlace
            ? ''
            : `${parseFloat(latitudeParam) || defaultLat}`,
          longitude: initialPlace
            ? ''
            : `${parseFloat(longitudeParam) || defaultLng}`,
          distance: initialDistance,
        },
        initialPlace?.name || queryParam
      );
    };

    handleInitialSearchOnLoad();
  }, []);

  const handleSearchByCurrentLocation = async () => {
    const location = window.navigator && window.navigator.geolocation;
    if (!location) return;

    setIsLoading(true);
    location.getCurrentPosition(
      async (position) => {
        updateDataLayer({
          event: 'searchBar',
          search_term: location,
          content_type: 'store_listing_location',
          page_type: 'Store Locator',
        });

        await handleSearch({
          endpoint: searchEndpoint,
          countryCode,
          languageCode: language,
          latitude: `${position.coords.latitude}`,
          longitude: `${position.coords.longitude}`,
          distance: currentDistance,
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  const handleSearchByUserInput = async (selectedDistance?: number) => {
    setIsLoading(true);

    const searchedPlace =
      currentPlace || (await getFirstAutocompletePlace(currentQueryText));
    const { latitude: latitudeParam = '', longitude: longitudeParam = '' } =
      qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }) as IStoreLocatorQueryParams;

    await handleSearch(
      {
        endpoint: searchEndpoint,
        input: searchedPlace ? '' : currentQueryText,
        countryCode,
        languageCode: language,
        placeId: searchedPlace ? searchedPlace.id : '',
        latitude: searchedPlace
          ? ''
          : `${parseFloat(latitudeParam) || defaultLat}`,
        longitude: searchedPlace
          ? ''
          : `${parseFloat(longitudeParam) || defaultLng}`,
        distance: selectedDistance || currentDistance,
      },
      searchedPlace?.name || currentQueryText
    );
  };

  return (
    <div
      data-testid="CT-30-Store-Locator-Search-Bar"
      className="relative z-40 flex flex-col items-center justify-center bg-white max-lg:sticky max-lg:top-0 max-lg:pt-2.5 lg:mb-4 lg:flex-row lg:justify-start lg:rounded-[10px] lg:bg-black-100 lg:px-13 lg:py-10 xl:justify-center"
    >
      <LoadingOverlay isLoading={isLoading} />

      <div className="flex w-full flex-col flex-wrap max-lg:gap-y-5 lg:flex-row lg:gap-x-7.5">
        <div className="relative flex items-center max-lg:w-full max-lg:flex-col max-lg:gap-y-5 lg:w-[305px] xl:w-[505px]">
          <div className="relative w-full">
            <StoreLocatorAutosuggest
              autocompleteEndpoint={autocompleteEndpoint}
              className="rounded-[10px] bg-grey-100 pr-14 lg:rounded-[5px] lg:bg-white lg:pr-[54px]"
              countryCode={countryCode}
              dataTestId="CT-30-Store-Locator-Autosuggest-Field"
              queryText={currentQueryText}
              searchPlaceholderLabel={searchPlaceholderLabel}
              type="locator_page"
              onSetPlace={setCurrentPlace}
              onSetQueryText={setCurrentQueryText}
              onSearch={handleSearchByUserInput}
            />

            <button
              aria-label="Search button"
              className={classNames(
                'absolute top-0 right-2 flex h-full w-12 appearance-none items-center justify-center rounded-[10px] border-none lg:hidden',
                {
                  'pointer-events-none': isInitialLoading,
                }
              )}
              onClick={() => {
                handleSearchByUserInput();
                updateDataLayer({
                  event: 'searchBar',
                  search_term: currentQueryText,
                  content_type: 'store_listing',
                  page_type: 'Store Locator',
                });
              }}
              data-testid="CT-30-Store-Locator-Mobile-Search-Button"
            >
              <Icon name="search" size="1.5rem" />
            </button>
          </div>

          <button
            aria-label="Current location button"
            className={classNames(
              'flex cursor-pointer items-center rounded-[10px] max-lg:h-12 max-lg:w-full max-lg:gap-x-2.5 max-lg:border max-lg:border-grey max-lg:px-5 lg:absolute lg:top-0 lg:right-0 lg:h-full lg:w-[52px] lg:appearance-none lg:justify-center lg:border-none',
              {
                'pointer-events-none': isInitialLoading,
              }
            )}
            onClick={handleSearchByCurrentLocation}
            data-testid="CT-30-Store-Locator-Location-Button"
          >
            <Icon
              name="location"
              size="1.5rem"
              className="ease transition-transform duration-300 hover:scale-95"
            />

            <p className="body-three text-grey-450 lg:hidden">
              {useMyCurrentLocationLabel}
            </p>
          </button>
        </div>

        <div
          className="relative flex max-lg:justify-between max-lg:gap-x-6 max-lg:rounded-t-[10px] max-lg:bg-black-100 max-lg:py-3 max-lg:pr-6 max-lg:pl-4 lg:items-center lg:gap-x-1.5"
          data-testid="CT-30-Store-Locator-Distance-Select-Field"
        >
          <div className="flex shrink-0 gap-x-2 lg:hidden">
            <button
              aria-label="List view button"
              onClick={() => {
                onSetIsListView(true);
              }}
              className="flex p-2 transition-colors"
              data-testid="CT-30-Store-Locator-Mobile-List-View-Button"
            >
              <Icon
                name="list"
                colorClassName={isListView ? 'text-yellow' : 'text-white'}
              />
            </button>

            <button
              aria-label="Map view button"
              onClick={() => {
                onSetIsListView(false);
              }}
              className="flex p-2 transition-colors"
              data-testid="CT-30-Store-Locator-Mobile-Map-View-Button"
            >
              <Icon
                name="map"
                colorClassName={isListView ? 'text-white' : 'text-yellow'}
              />
            </button>
          </div>

          <p className="body-two text-white max-lg:hidden">{withinLabel}</p>

          <div className="relative flex items-center">
            <select
              className={classNames(
                'body-two appearance-none border-none bg-inherit pr-6 text-white max-lg:h-full lg:cursor-pointer lg:py-2.5 [&>option]:text-black',
                {
                  'outline-none': !isTabInteraction,
                }
              )}
              aria-label="Search radius select"
              onChange={(e) => {
                const distanceValue = e.target.value;
                const newDistance = parseInt(distanceValue, 10);
                setCurrentDistance(newDistance);
                updateDataLayer({
                  event: 'filterUsed',
                  content_type: 'store_listing',
                  filter_action: 'apply_filter',
                  filter: [
                    {
                      filter_category: 'Search Radius',
                      filter_name: distanceValue,
                    },
                  ],
                });

                handleSearchByUserInput(newDistance);
              }}
              value={currentDistance}
            >
              {distances.map((distance) => (
                <option key={distance} value={distance}>
                  {`${
                    isMobile ? `${withinLabel} ` : ''
                  }${distance} ${distanceMeasurementUnitLabel}`}
                </option>
              ))}
            </select>

            <div
              aria-label="Caret down icon"
              className="pointer-events-none absolute right-0 flex"
            >
              <Icon name="caret_down" colorClassName="text-yellow" />
            </div>
          </div>
        </div>
      </div>

      <Button
        className={classNames('max-lg:hidden', {
          'pointer-events-none': isInitialLoading,
        })}
        onClick={() => {
          handleSearchByUserInput();
          updateDataLayer({
            event: 'searchBar',
            search_term: currentQueryText,
            content_type: 'store_listing',
            page_type: 'Store Locator',
          });
        }}
        dataTestId="CT-30-Store-Locator-Desktop-Search-Button"
      >
        {searchButtonLabel}
      </Button>
    </div>
  );
};

export default StoreLocatorSearchBar;
